import { useState, useRef } from "react";
import {
  Box, Typography, Button, LinearProgress,
  Alert, Chip
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axios from "axios";

export default function VideoUpload({ moduleId, onUploadComplete }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (selected.name.match(/\.avi$/i)) {
      setError("AVI format is not supported by web browsers. Please convert your video to MP4 format before uploading.");
      return;
    }
    if (!selected.name.match(/\.(mp4|mov|mpeg|mp3|wav|ogg|m4a|pdf|txt|doc|docx)$/i)) {
      setError("Invalid file type. Allowed: MP4, MOV, MP3, WAV, OGG, M4A, PDF, TXT, DOC, DOCX.");
      return;
    }

    if (selected.size > 500 * 1024 * 1024) {
      setError("File too large. Maximum size is 500MB.");
      return;
    }

    setFile(selected);
    setError("");
    setSuccess("");
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setError("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("media_title", file.name.replace(/\.[^/.]+$/, "").replace(/_/g, " "));

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/upload/media/${moduleId}`,
        formData,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setProgress(percent);
          }
        }
      );

      setSuccess(`File uploaded successfully — ${response.data.file_size_mb} MB`);
      setFile(null);
      if (onUploadComplete) onUploadComplete(response.data);
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/mpeg,video/quicktime,audio/mpeg,audio/wav,audio/mp3,application/pdf,text/plain,.mp4,.mov,.mp3,.wav,.pdf,.txt,.doc,.docx"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {!file ? (
        <Box
          onClick={() => fileInputRef.current.click()}
          sx={{
            border: "2px dashed #1a237e",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: "#f5f7fa",
            transition: "all 0.2s",
            "&:hover": { bgcolor: "#e8eaf6", borderColor: "#0d47a1" }
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: "#1a237e", mb: 1 }} />
          <Typography variant="body1" fontWeight={600} color="#1a237e">
            Click to select a file
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Video (MP4/MOV), Audio (MP3/WAV), PDF or Document (TXT/DOC/DOCX) — Maximum 500MB
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          p: 3,
          bgcolor: "#f5f7fa"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <VideoFileIcon sx={{ fontSize: 40, color: "#1a237e" }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1" fontWeight={600}>{file.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatSize(file.size)}
              </Typography>
            </Box>
            <Chip label="Ready to upload" color="info" size="small" />
          </Box>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2">Uploading...</Typography>
                <Typography variant="body2" fontWeight={600}>{progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress}
                sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={uploading ? null : <CloudUploadIcon />}
              onClick={handleUpload}
              disabled={uploading}
              sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}
            >
              {uploading ? `Uploading ${progress}%...` : "Upload File"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => { setFile(null); setError(""); }}
              disabled={uploading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
